import { useState } from "react";
import { Star } from "lucide-react";
import { StarRatingProps } from "./feedback.types";

// Five-point star silhouette for the "solid" variant — lucide's Star is
// outline-first (rounded joins, inner stroke) and reads differently when
// filled, so the solid look gets its own sharp-pointed path.
function SolidStar({ size, className }: { size: number; className: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M12 2.5l2.9 6.4 6.6.7-4.9 4.5 1.4 6.9L12 18l-6 3.4 1.4-6.9L2.5 9.6l6.6-.7L12 2.5Z" />
    </svg>
  );
}

export function StarRating({
  value,
  onChange,
  max = 5,
  disabled = false,
  size = 24,
  variant = "outline",
  filledClassName = "text-brand-primary fill-brand-primary",
  emptyClassName = variant === "solid"
    ? "text-[#D6D3CB]"
    : "text-content-disabled fill-none",
}: StarRatingProps) {
  const [hoverValue, setHoverValue] = useState(0);

  const displayValue = hoverValue || value;
  const isSolid = variant === "solid";

  // Solid variant mirrors the design handoff's `.stars button` exactly:
  // no padding, line-height 0, color+transform tween at 140ms on the
  // design easing, hover lifts the star to 1.12×. Outline variant keeps
  // the original padded/rounded chrome used by the feedback dialog.
  const buttonClassName = isSolid
    ? "leading-[0] transition-[color,transform] duration-[140ms] ease-[cubic-bezier(0.32,0.72,0,1)] enabled:hover:scale-[1.12] focus:outline-none focus-visible:ring-2 focus-visible:ring-border-focus rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
    : "p-0.5 rounded transition-[color,transform] enabled:hover:scale-[1.12] focus:outline-none focus-visible:ring-2 focus-visible:ring-border-focus disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <div
      className={`flex items-center ${isSolid ? "gap-2" : "gap-1"}`}
      onMouseLeave={() => setHoverValue(0)}
    >
      {Array.from({ length: max }, (_, i) => {
        const starIndex = i + 1;
        const isFilled = starIndex <= displayValue;

        return (
          <button
            key={starIndex}
            type="button"
            disabled={disabled}
            className={buttonClassName}
            onMouseEnter={() => !disabled && setHoverValue(starIndex)}
            onClick={() => {
              if (disabled) return;
              onChange(value === starIndex ? 0 : starIndex);
            }}
            aria-label={`Rate ${starIndex} of ${max}`}
          >
            {variant === "solid" ? (
              <SolidStar
                size={size}
                className={isFilled ? filledClassName : emptyClassName}
              />
            ) : (
              <Star
                className={isFilled ? filledClassName : emptyClassName}
                size={size}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
